import { Card, CardContent, Link, Stack, Typography } from '@mui/material';

const ALTERNATIVE_LINKS: ReadonlyArray<{ readonly label: string; readonly to: string }> = [
  { label: 'Daily', to: '/' },
  { label: 'Days', to: '/days' },
  { label: 'Games', to: '/games' },
  { label: 'Newsletter', to: '/newsletter' },
  { label: 'Podcast', to: '/podcast' },
];

/**
 * Fallback strings used until `BrandingConfig.copy.emptyResults` lands.
 *
 * **Branding gap.** Requirement 8.1 + 8.4 reference
 * `BrandingConfig.copy.emptyResults.title` and
 * `.suggestion`; the `BrandingTokens` shape published by
 * `models/src/branding-config.ts` does not yet declare those keys
 * (gated on TODO-013 — brand assets). The strings below are the
 * default English values from Requirement 8.4 and are replaced with a
 * `brandingFixture.copy.emptyResults.{title,suggestion}` read once
 * the contract surface adds the keys.
 */
const FALLBACK_TITLE = 'Nothing to show here yet.';
const FALLBACK_SUGGESTION = 'Try one of the surfaces below to keep exploring.';

/**
 * `<EmptyResults>` — rendered on a successful 200 with an empty
 * collection per Requirement 8.1. Surfaces the empty-state message
 * plus alternative-navigation links to `Daily`, `Days`, `Games`,
 * `Newsletter`, and `Podcast`.
 *
 * Note this component is **never** rendered on the API-failure path
 * — that surface is `<ApiUnavailableBanner>`. The two are mutually
 * exclusive per Requirement 8.3.
 *
 * _Requirements: Requirement 8.1, R8.11, design §6.2._
 */
export function EmptyResults(): JSX.Element {
  return (
    <Card>
      <CardContent>
        <Typography variant="h3" component="h3" gutterBottom>{FALLBACK_TITLE}</Typography>
        <Typography variant="body1" gutterBottom>{FALLBACK_SUGGESTION}</Typography>
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
          {ALTERNATIVE_LINKS.map((entry) => (
            <Link key={entry.to} href={entry.to}>{entry.label}</Link>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
